import { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'
import { useCallback, useMemo, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { NotificationController } from 'core-components/library/popup'
import { ConfirmationDialog } from 'partner360/components/confirmationDialog/ConfirmationDialog'
import { IJBPPlanning } from 'partner360/entities/planning/model'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { PLANNING_SMB_TRANSLATION_KEYS, SMB_CURRENCY_USD, SMB_FY_QUARTER } from '../../configs'
import {
  buildSmbRows,
  buildSmbTree,
  createPlanningSmbCategoryNotApplicableResolver,
  createPlanningSmbFinancialResolver,
  ensureQuarterSeed,
  ensureVerticalSeed,
  ensureVerticalSeedQuarters,
  getPlanningSmbLastPlanYear,
  getPlanningSmbYears,
  getSmbDefaultExpand,
  getSmbEffectiveValue,
  getSmbNodeId,
  isSmbNodeExcluded,
  mapPlanningSmbToStore,
  TGetSmbWeight,
  TSmbSectionSettings,
} from '../../lib'
import { IPlanningSmb, ISmbNode, TSmbSplitResult } from '../../model/types'
import { PlanningSmbCell } from '../planningSmbCell/PlanningSmbCell'
import { PlanningSmbGuidedSetup } from '../planningSmbGuidedSetup/PlanningSmbGuidedSetup'
import { PlanningSmbModal } from '../planningSmbModal/PlanningSmbModal'

const toFinancialValue = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0

export interface IPlanningSmbSwitcherProps {
  /** The control is available after every required Financial Plan item is complete. */
  isVisible: boolean
  formats: IFormActualsTargetsSettings[]
  /** Full raw Financial Plan/Actuals dataset used to resolve weights for every Category. */
  planningData: IJBPPlanning[]
  planningSmb: IPlanningSmb[]
  partnerId: string
  partnerName: string
  isDistributor: boolean
  fYear: number
  programType: string
  /** Category whose action opens/focuses the modal; omitted for the Offering total. */
  category?: string
  /** Planning section settings, the source of the per-category Not applicable flag */
  sectionSettings?: TSmbSectionSettings
  globalCategory?: string | null
  /** Metric and currency the Financial Plan itself is entered at */
  planActualsType: ActualsValueTypes
  planCurrency: string
  /** Metric and currency options Planning offers for this category */
  actualsTypes: ActualsValueTypes[]
  currencies: string[]
  /** Number scale and options owned by Planning; the modal edits this same state. */
  roundingValue: string
  roundingOptions: string[]
  onRoundingChange: (value: string) => void
  /** Current Quarterly Planning value from the Financial Planning category. */
  isQuarterlyPlanning: boolean
  /** The same mandatory-quarterly flag that disables the switch in Financial Planning. */
  isQuarterlyPlanningDisabled: boolean
  /** Publishing mode controls whether Plan / Update plan is rendered at all. */
  isEditMode: boolean
  isEditAllowed: boolean
  formatMoney: (value: number) => string
  /** Reloads the whole planning data set once the split has been saved */
  onSaved: () => void | Promise<void>
}

/**
 * Everything the planning widget needs for the SMB vs EU split: the status of the category
 * own split, the action that opens it and the split popup itself.
 */
export const PlanningSmbSwitcher = ({
  isVisible,
  formats,
  planningData,
  planningSmb,
  partnerId,
  partnerName,
  isDistributor,
  fYear,
  programType,
  category,
  sectionSettings,
  globalCategory,
  planActualsType,
  planCurrency,
  actualsTypes,
  currencies,
  roundingValue,
  roundingOptions,
  onRoundingChange,
  isQuarterlyPlanning,
  isQuarterlyPlanningDisabled,
  isEditMode,
  isEditAllowed,
  formatMoney,
  onSaved,
}: IPlanningSmbSwitcherProps) => {
  const t = useSafetyTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isGuidedModalOpen, setIsGuidedModalOpen] = useState(false)
  // Editing is refused rather than hidden: the reason is explained instead of a dead button
  const [isBlockedShown, setIsBlockedShown] = useState(false)

  const resolvedProgramType = useMemo(() => {
    const exactSetting = category
      ? formats.find(
          (setting) => setting.ProgramType === programType && setting.Category === category
        )
      : formats.find((setting) => setting.ProgramType === programType)

    return (
      exactSetting?.ProgramType ||
      (category && formats.find((setting) => setting.Category === category)?.ProgramType) ||
      (!category && formats[0]?.ProgramType) ||
      programType
    )
  }, [formats, programType, category])
  const lastPlanYear = useMemo(
    () =>
      getPlanningSmbLastPlanYear(planningData, {
        partnerId,
        programType: resolvedProgramType,
        fYear,
        globalCategory,
      }),
    [planningData, partnerId, resolvedProgramType, fYear, globalCategory]
  )
  const years = useMemo(() => getPlanningSmbYears(fYear, lastPlanYear), [fYear, lastPlanYear])
  const baselineYear = fYear - 1

  // Categories switched off as Not applicable in Planning are marked while the model is
  // built, so the whole editor treats them as out of the plan from the very first render.
  // Rows the back end reports as disabled cannot hold a split, whichever level they sit on
  const disabledNodeIds = useMemo(
    () =>
      new Set(
        planningSmb
          .filter(
            (row) =>
              row.IsDisabled === 1 &&
              row.PartnerID === partnerId &&
              row.ProgramType === resolvedProgramType &&
              years.includes(row.FYear)
          )
          .map((row) =>
            getSmbNodeId(row.ProgramType, row.Category, row.SubCategory, row.L3Category)
          )
      ),
    [planningSmb, partnerId, resolvedProgramType, years]
  )
  const isCategoryNotApplicable = useMemo(
    () =>
      createPlanningSmbCategoryNotApplicableResolver({
        formats,
        planningData,
        partnerId,
        programType: resolvedProgramType,
        fYear,
        globalCategory,
        sectionSettings: sectionSettings || {},
      }),
    [formats, planningData, partnerId, resolvedProgramType, fYear, globalCategory, sectionSettings]
  )
  const tree = useMemo(
    () => buildSmbTree(formats, resolvedProgramType, isCategoryNotApplicable, disabledNodeIds),
    [formats, resolvedProgramType, isCategoryNotApplicable, disabledNodeIds]
  )
  const baselineStore = useMemo(
    () =>
      mapPlanningSmbToStore(
        planningSmb,
        { partnerId, programType: resolvedProgramType, years: [baselineYear] },
        tree || undefined
      ),
    [planningSmb, partnerId, resolvedProgramType, baselineYear, tree]
  )
  const baselineExpandedNodeIds = useMemo(() => {
    if (!tree) {
      return new Set<string>()
    }

    const expanded = getSmbDefaultExpand(tree)

    return new Set(Object.keys(expanded).filter((nodeId) => expanded[nodeId]))
  }, [tree])
  const focusNode = useMemo(
    () =>
      category
        ? tree?.children.find((node) => node.kind === 'category' && node.category === category)
        : tree || undefined,
    [tree, category]
  )

  const getFinancialValue = useMemo(
    () => createPlanningSmbFinancialResolver(formats, planningData, partnerId, globalCategory),
    [formats, planningData, partnerId, globalCategory]
  )

  const getWeight = useCallback<TGetSmbWeight>(
    function resolveWeight(node: ISmbNode, year: number, fq: number): number {
      if (node.settingsIndex >= 0) {
        return toFinancialValue(
          getFinancialValue({
            settingsIndex: node.settingsIndex,
            fq,
            type: 'targets',
            fYear: year,
            actualsType: planActualsType,
            isUSD: planCurrency === SMB_CURRENCY_USD,
            smbFraction: 'Total',
          })
        )
      }

      return node.children
        .filter((child) => !isSmbNodeExcluded(child))
        .reduce((total, child) => total + resolveWeight(child, year, fq), 0)
    },
    [getFinancialValue, planActualsType, planCurrency]
  )

  const getDirectBaselineAmount = useCallback(
    (node: ISmbNode, fq: number) =>
      node.settingsIndex < 0
        ? 0
        : toFinancialValue(
            getFinancialValue({
              settingsIndex: node.settingsIndex,
              fq,
              type: 'actuals',
              fYear: baselineYear,
              actualsType: planActualsType,
              isUSD: planCurrency === SMB_CURRENCY_USD,
              smbFraction: 'Total',
            })
          ),
    [baselineYear, getFinancialValue, planActualsType, planCurrency]
  )

  const getBaselineWeight = useCallback<TGetSmbWeight>(
    function resolveBaselineWeight(node: ISmbNode, year: number, fq: number): number {
      if (node.children.length) {
        return node.children
          .filter((child) => !isSmbNodeExcluded(child))
          .reduce((total, child) => total + resolveBaselineWeight(child, year, fq), 0)
      }

      return getDirectBaselineAmount(node, fq)
    },
    [getDirectBaselineAmount]
  )

  const getBaselineValue = useCallback(
    (node: ISmbNode): TSmbSplitResult =>
      getSmbEffectiveValue(baselineStore, node, baselineYear, SMB_FY_QUARTER, getBaselineWeight, {
        ignoreDirectNodeIds: baselineExpandedNodeIds,
        allowPartialChildren: true,
      }),
    [baselineStore, baselineYear, getBaselineWeight, baselineExpandedNodeIds]
  )

  const summaries = useMemo(() => {
    const empty = { isEmpty: true } as const

    if (!tree || !focusNode || isSmbNodeExcluded(focusNode)) {
      return { focus: empty, offering: empty }
    }

    const store = mapPlanningSmbToStore(
      planningSmb,
      {
        partnerId,
        programType: resolvedProgramType,
        years,
      },
      tree
    )
    const expanded = getSmbDefaultExpand(tree)
    const rows = buildSmbRows(tree, expanded)
    const expandedNodeIds = new Set(rows.filter((row) => row.isExpanded).map((row) => row.node.id))

    rows.forEach(({ node, ancestors, isExpanded }) => {
      if (isExpanded || isSmbNodeExcluded(node)) {
        return
      }

      if (isQuarterlyPlanning) {
        ensureVerticalSeedQuarters(store, node, ancestors, fYear, getWeight)
        ensureQuarterSeed(store, node.id, fYear)
      } else {
        ensureVerticalSeed(store, node, ancestors, fYear, SMB_FY_QUARTER, getWeight)
      }
    })

    const resolveSummary = (node: ISmbNode) =>
      getSmbEffectiveValue(store, node, fYear, SMB_FY_QUARTER, getWeight, {
        deriveFyFromQuarters: isQuarterlyPlanning,
        ignoreDirectNodeIds: expandedNodeIds,
      })
    const focus = resolveSummary(focusNode)

    return {
      focus,
      offering: focusNode.id === tree.id ? focus : resolveSummary(tree),
    }
  }, [
    tree,
    focusNode,
    planningSmb,
    partnerId,
    fYear,
    resolvedProgramType,
    years,
    getWeight,
    isQuarterlyPlanning,
  ])
  const baselineSummaries = useMemo(() => {
    const empty = { isEmpty: true } as const

    if (!tree || !focusNode || isSmbNodeExcluded(focusNode)) {
      return { focus: empty, offering: empty }
    }

    const focus = getBaselineValue(focusNode)

    return {
      focus,
      offering: focusNode.id === tree.id ? focus : getBaselineValue(tree),
    }
  }, [focusNode, getBaselineValue, tree])

  const handleSaveFailed = useCallback(() => {
    NotificationController.showError({
      message: t(PLANNING_SMB_TRANSLATION_KEYS.notifications.saveFailed),
    })
  }, [t])

  if (isDistributor || !isVisible || !tree || !focusNode) {
    return null
  }

  return (
    <>
      <PlanningSmbCell
        summary={summaries.focus}
        baselineSummary={baselineSummaries.focus}
        showAction={isEditMode}
        onOpen={() => (isEditAllowed ? setIsModalOpen(true) : setIsBlockedShown(true))}
        showGuidedAction={isEditMode}
        onOpenGuided={() => (isEditAllowed ? setIsGuidedModalOpen(true) : setIsBlockedShown(true))}
      />
      {isBlockedShown && (
        <ConfirmationDialog
          isVisible
          isOnlyApply
          title={PLANNING_SMB_TRANSLATION_KEYS.blocked.title}
          confirmBtnText={PLANNING_SMB_TRANSLATION_KEYS.blocked.confirm}
          content={<Translation text={PLANNING_SMB_TRANSLATION_KEYS.blocked.message} />}
          onConfirm={() => setIsBlockedShown(false)}
        />
      )}
      {isModalOpen && (
        <PlanningSmbModal
          show
          tree={tree}
          planningSmb={planningSmb}
          partnerId={partnerId}
          fYear={fYear}
          programType={resolvedProgramType}
          focusNodeId={focusNode.id}
          lastPlanYear={lastPlanYear}
          planActualsType={planActualsType}
          planCurrency={planCurrency}
          actualsTypes={actualsTypes}
          currencies={currencies}
          roundingValue={roundingValue}
          roundingOptions={roundingOptions}
          onRoundingChange={onRoundingChange}
          isQuarterlyPlanning={isQuarterlyPlanning}
          isQuarterlyPlanningDisabled={isQuarterlyPlanningDisabled}
          offeringSummary={summaries.offering}
          offeringBaseline={baselineSummaries.offering}
          getFinancialValue={getFinancialValue}
          formatMoney={formatMoney}
          onSaved={onSaved}
          onSaveFailed={handleSaveFailed}
          onSwitchGuided={() => {
            setIsModalOpen(false)
            setIsGuidedModalOpen(true)
          }}
          onClose={() => setIsModalOpen(false)}
        />
      )}
      {isGuidedModalOpen && (
        <PlanningSmbGuidedSetup
          show
          tree={tree}
          planningSmb={planningSmb}
          partnerId={partnerId}
          partnerName={partnerName || partnerId}
          lastPlanYear={lastPlanYear}
          fYear={fYear}
          programType={resolvedProgramType}
          planActualsType={planActualsType}
          planCurrency={planCurrency}
          actualsTypes={actualsTypes}
          currencies={currencies}
          roundingValue={roundingValue}
          roundingOptions={roundingOptions}
          onRoundingChange={onRoundingChange}
          isQuarterlyPlanning={isQuarterlyPlanning}
          isQuarterlyPlanningDisabled={isQuarterlyPlanningDisabled}
          getFinancialValue={getFinancialValue}
          formatMoney={formatMoney}
          offeringSummary={summaries.offering}
          offeringBaseline={baselineSummaries.offering}
          getWeight={getWeight}
          getBaselineValue={getBaselineValue}
          onSaved={onSaved}
          onSaveFailed={handleSaveFailed}
          onSwitchClassic={() => {
            setIsGuidedModalOpen(false)
            setIsModalOpen(true)
          }}
          onClose={() => setIsGuidedModalOpen(false)}
        />
      )}
    </>
  )
}
